import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { Plus, Play, RefreshCw } from 'lucide-react';

const Rent = () => {
    const [payments, setPayments] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [p, t] = await Promise.all([
                axios.get('/api/rent'),
                axios.get('/api/tenants').catch(() => ({ data: { tenants: [] } })),
            ]);
            setPayments(p.data.payments || []);
            setTenants(t.data.tenants || t.data.data || []);
        } catch { toast.error('Failed to load payments'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const runInvoices = async () => {
        const t = toast.loading('Generating invoices…');
        try {
            const { data } = await axios.post('/api/ledger/jobs/generate-invoices');
            toast.success(`Created ${data.created} invoice(s)`, { id: t });
        } catch (e) { toast.error(e.response?.data?.message || 'Failed', { id: t }); }
    };

    const runLateFees = async () => {
        const t = toast.loading('Applying late fees…');
        try {
            const { data } = await axios.post('/api/ledger/jobs/late-fees');
            toast.success(`Applied to ${data.applied} invoice(s)`, { id: t });
        } catch (e) { toast.error(e.response?.data?.message || 'Failed', { id: t }); }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h1 className="text-2xl font-bold">Rent payments</h1>
                    <p className="text-gray-600">Manual entries + payments posted via bank/M-Pesa webhooks.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={runInvoices} className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm">
                        <Play className="h-4 w-4" /> Generate invoices
                    </button>
                    <button onClick={runLateFees} className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm">
                        <RefreshCw className="h-4 w-4" /> Apply late fees
                    </button>
                    <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg">
                        <Plus className="h-4 w-4" /> Record payment
                    </button>
                </div>
            </div>

            {loading ? <div>Loading…</div> : (
                <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left">
                            <tr>
                                <th className="p-3">Date</th>
                                <th className="p-3">Tenant</th>
                                <th className="p-3">Property</th>
                                <th className="p-3">Method</th>
                                <th className="p-3">Ref</th>
                                <th className="p-3">Amount</th>
                                <th className="p-3">Receipt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.length === 0 && (
                                <tr><td colSpan={7} className="p-6 text-center text-gray-500">No payments yet.</td></tr>
                            )}
                            {payments.map(p => (
                                <tr key={p.id} className="border-t">
                                    <td className="p-3">{new Date(p.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                    <td className="p-3">{p.tenant_name}</td>
                                    <td className="p-3">{p.property_name}</td>
                                    <td className="p-3 capitalize">{p.payment_method?.replace('_',' ')}</td>
                                    <td className="p-3 font-mono text-xs">{p.reference_number || '—'}</td>
                                    <td className="p-3">{formatCurrency(p.amount)}</td>
                                    <td className="p-3 font-mono text-xs">{p.receipt_number || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && <RecordPaymentForm tenants={tenants} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
        </div>
    );
};

const RecordPaymentForm = ({ tenants, onClose, onSaved }) => {
    const [f, setF] = useState({ tenant_id:'', property_id:'', amount:'', payment_method:'cash', payment_date:'', reference_number:'', notes:'' });
    const [saving, setSaving] = useState(false);
    const set = (k,v) => setF(x => ({ ...x, [k]: v }));

    const onTenantChange = (id) => {
        const t = tenants.find(t => String(t.id) === String(id));
        set('tenant_id', id);
        if (t) { set('property_id', t.property_id); if (!f.amount) set('amount', t.monthly_rent); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!f.tenant_id || !f.property_id || !f.amount) { toast.error('Tenant and amount required'); return; }
        setSaving(true);
        try {
            const { data } = await axios.post('/api/rent', f);
            toast.success(`Payment recorded (receipt ${data.receipt?.receipt_number || ''})`);
            onSaved();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-white rounded-xl max-w-lg w-full shadow-xl">
                <div className="p-4 border-b flex justify-between"><h2 className="font-semibold">Record payment</h2><button type="button" onClick={onClose}>✕</button></div>
                <div className="p-4 space-y-3">
                    <select required className="w-full border rounded-lg px-3 py-2" value={f.tenant_id} onChange={e => onTenantChange(e.target.value)}>
                        <option value="">Select tenant…</option>
                        {tenants.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name} — {t.property_name}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-3">
                        <input required type="number" step="0.01" placeholder="Amount" className="border rounded-lg px-3 py-2" value={f.amount} onChange={e => set('amount', e.target.value)} />
                        <input type="date" className="border rounded-lg px-3 py-2" value={f.payment_date} onChange={e => set('payment_date', e.target.value)} />
                    </div>
                    <select className="w-full border rounded-lg px-3 py-2" value={f.payment_method} onChange={e => set('payment_method', e.target.value)}>
                        {['cash','check','bank_transfer','credit_card','online'].map(m => <option key={m} value={m}>{m.replace('_',' ')}</option>)}
                    </select>
                    <input placeholder="Reference number" className="w-full border rounded-lg px-3 py-2" value={f.reference_number} onChange={e => set('reference_number', e.target.value)} />
                    <textarea placeholder="Notes" className="w-full border rounded-lg px-3 py-2" rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} />
                </div>
                <div className="p-4 border-t flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
                    <button disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                </div>
            </form>
        </div>
    );
};

export default Rent;
